import React, { useState, useRef, useCallback } from "react";
import { Form, Select } from "antd";
import { useTranslation } from "react-i18next";

interface CustomTagProps {
  label: React.ReactNode;
  value: any;
  closable: boolean;
  onClose: (event?: React.MouseEvent<HTMLElement>) => void;
}

export default function BenchMarksForm({
  details,
  name,
}: {
  details: any;
  name: string | string[];
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const currentValue = Form.useWatch(name, form) || [];
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItemRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  const dragOverIndexRef = useRef<number | null>(null);
  const currentValueRef = useRef(currentValue);

  // 同步 ref
  React.useEffect(() => {
    currentValueRef.current = currentValue;
  }, [currentValue]);

  React.useEffect(() => {
    dragOverIndexRef.current = dragOverIndex;
  }, [dragOverIndex]);

  const handleMouseDown = useCallback((e: React.MouseEvent, index: number) => {
    // 如果点击的是关闭按钮，不触发拖拽
    if ((e.target as HTMLElement).closest('.ant-select-selection-item-remove')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    dragItemRef.current = index;
    setDraggedIndex(index);
    isDraggingRef.current = true;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || dragItemRef.current === null) return;

      // 找到当前 Select 的所有标签元素 - 从触发事件的元素向上查找
      const targetElement = e.target as HTMLElement;
      const selectContainer = targetElement.closest('.ant-select-selector') || 
                              document.querySelector('.ant-select-selector');
      
      if (!selectContainer) return;

      const tags = Array.from(selectContainer.querySelectorAll('.ant-select-selection-item'));
      let targetIndex: number | null = null;

      tags.forEach((tag, idx) => {
        const rect = tag.getBoundingClientRect();
        
        // 检查鼠标是否在这个标签上方
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          const centerX = rect.left + rect.width / 2;
          // 根据鼠标位置决定插入位置
          if (e.clientX < centerX) {
            targetIndex = idx;
          } else {
            targetIndex = idx + 1;
          }
        }
      });

      if (targetIndex !== null && targetIndex !== dragItemRef.current) {
        setDragOverIndex(targetIndex);
      } else if (targetIndex === null) {
        setDragOverIndex(null);
      }
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current || dragItemRef.current === null) {
        cleanup();
        return;
      }

      const dragIndex = dragItemRef.current;
      const dropIndex = dragOverIndexRef.current;
      const value = currentValueRef.current;

      if (
        dropIndex !== null &&
        dragIndex !== null &&
        dragIndex !== dropIndex &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        const newValue = [...value];
        const [draggedItem] = newValue.splice(dragIndex, 1);
        const finalDropIndex = dropIndex > dragIndex ? dropIndex - 1 : dropIndex;
        newValue.splice(finalDropIndex, 0, draggedItem);
        form.setFieldValue(name, newValue);
      }

      cleanup();
    };

    const cleanup = () => {
      isDraggingRef.current = false;
      setDraggedIndex(null);
      setDragOverIndex(null);
      dragItemRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [form, name]);

  const tagRender = useCallback((props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const index = Array.isArray(currentValue) ? currentValue.indexOf(value) : -1;
    const isDragging = draggedIndex === index;
    const isDragOver = dragOverIndex === index;

    return (
      <span
        onMouseDown={(e) => {
          e.stopPropagation();
          handleMouseDown(e, index);
        }}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          transition: "all 0.2s",
          opacity: isDragging ? 0.6 : 1,
          transform: isDragOver ? "translateY(-2px)" : "none",
          boxShadow: isDragOver ? "0 2px 4px rgba(24, 144, 255, 0.2)" : "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          ...(isDragOver && {
            background: "#e6f7ff",
            borderColor: "#1890ff",
          }),
        }}
        className="ant-select-selection-item"
      >
        <span className="ant-select-selection-item-content">{label}</span>
        {closable && (
          <span
            className="ant-select-selection-item-remove"
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              fontSize: "16px",
              lineHeight: "1",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "4px",
              cursor: "pointer",
              opacity: 0.6,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "0.6";
            }}
          >
            ×
          </span>
        )}
      </span>
    );
  }, [currentValue, draggedIndex, dragOverIndex, handleMouseDown]);

  return (
    <div className="">
      <style>{`
        .ant-select-selection-overflow {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
        }
        .ant-select-selection-overflow-item {
          display: inline-flex;
          align-items: center;
        }
        /* 确保 tag 在输入框内垂直居中 */
        .ant-select-selection-item {
          display: inline-flex;
          align-items: center;
          vertical-align: middle;
        }
        /* 关闭图标样式优化 */
        .ant-select-selection-item-remove {
          font-size: 16px !important;
          line-height: 1 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          margin-left: 4px !important;
          cursor: pointer !important;
          opacity: 0.6;
          transition: opacity 0.2s;
        }
        .ant-select-selection-item-remove:hover {
          opacity: 1 !important;
        }
      `}</style>
      <div className="flex items-center mb-2 gap-2">
        <div className="">
          <span className="text-[#1c2024] text-sm font-medium  leading-snug">
            {t("campaignDetail.campaignInformationEdit.benchmarkBrands")}
          </span>
          <span className="text-[#7f1d1d] text-sm font-medium  leading-snug">
            *
          </span>
        </div>
      </div>
      <Form.Item
        name={name}
        rules={[
          {
            required: true,
            message: t(
              "campaignDetail.campaignInformationEdit.pleaseEnterBenchmarkBrands"
            ),
          },
        ]}
        className="mb-2"
      >
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder={t(
            "campaignDetail.campaignInformationEdit.benchmarkBrandsPlaceholder"
          )}
          options={details?.product?.benchmarks?.map((item: string) => ({
            value: item,
            label: item,
          }))}
          tagRender={tagRender}
        />
      </Form.Item>
      <div className="justify-start text-[#79716B] text-sm font-normal font-['DM_Sans'] leading-snug">
        {t("campaignCreate.businessInformation.benchmarkBrandsDescription", {
          defaultValue:
            "We’ll use your benchmark brands to match both influencers who are a great fit to promote them and those who’ve partnered with these brands before.",
        })}
      </div>
    </div>
  );
}
